import { Box } from '@mui/material';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import { Scenarios, Lanes } from '@gql/base-types';
import { ScheduleTimelineRow } from '../hooks/useScheduleData';
import ScheduleScenarioBlock from './ScheduleScenarioBlock';
import ScheduleOfferedScenarioBlock from './ScheduleOfferedScenarioBlock';
import ScheduleOptimisticBlock from './ScheduleOptimisticBlock';
import SchedulePlanningOverlay from './SchedulePlanningOverlay';
import DriverScheduleOverlay from './DriverScheduleOverlay';
import { getGapBetweenTimes } from '@components/Timeline/Timeline/utils';
import { ISchedulingAlgorithm } from '@hopdrive/sdk-appointments';
import { useDriverSchedule } from '@features/drivers/hooks/useDriverSchedule';
import { processDriverSchedule } from '@features/drivers/utils/driverScheduleUtils';
import { getScenarioDisplayRange } from '../utils/scenarioTimeUtils';
import { isLiteTier, isStandardTier } from '@utils/tiers';
import { useOptimisticMoves } from '../context/OptimisticMoveContext';

interface ScheduleAreaRowProps {
  dataItem: ScheduleTimelineRow;
  index: number;
  isLastItem: boolean;
  rowSize: number;
  timezoneOverride: string;
  zoom: number;
  date: string;
  onScenarioClick: (scenario: Scenarios) => void;
  planningLane?: Lanes | null;
  onTimeSlotClick?: (startTime: Date, driverId?: number) => void;
  moveBufferMinutes?: number;
  dayStartTime?: string; // Format: HH:mm:ss (e.g., "09:00:00")
  dayEndTime?: string; // Format: HH:mm:ss (e.g., "17:00:00")
  algorithm?: ISchedulingAlgorithm;
  driverCount?: number;
  allDriverScenarios?: Scenarios[][];
  tier?: 'standard' | 'lite';
  offsetIncrementMinutes?: number;
  offsetMaxMinutes?: number;
  scenarioKey?: string; // Scenario type being planned (e.g., 'concierge-pu', 'concierge-ret')
  globalRealMoveIds?: Set<number>; // All move IDs across all rows (for optimistic block visibility)
}

const ScheduleAreaRow = ({
  dataItem,
  index,
  rowSize,
  timezoneOverride,
  zoom,
  date,
  onScenarioClick,
  planningLane,
  onTimeSlotClick,
  moveBufferMinutes = 0,
  dayStartTime,
  dayEndTime,
  algorithm,
  driverCount,
  allDriverScenarios,
  tier,
  offsetIncrementMinutes = 0,
  offsetMaxMinutes = 0,
  scenarioKey,
  globalRealMoveIds,
}: ScheduleAreaRowProps) => {
  // Fetch driver schedule for this driver (Lite tier only)
  const driverId = isLiteTier(tier) ? dataItem.driver?.id?.toString() : undefined;
  const { driverSchedule } = useDriverSchedule(driverId);

  // Get optimistic moves for this driver row
  // For Lite tier: match by driver ID
  // For Standard tier: show moves with driverId: null on the first hypothetical driver row (index 0)
  const { optimisticMoves } = useOptimisticMoves();
  const driverOptimisticMoves = useMemo(() => {
    return optimisticMoves.filter((move) => {
      // Lite tier: match by driver ID
      if (move.driverId !== null && dataItem.driver?.id) {
        return move.driverId === dataItem.driver.id;
      }
      // Standard tier: show unassigned moves (driverId: null) on first hypothetical driver row
      if (move.driverId === null && isStandardTier(move.tier) && index === 0 && dataItem.driver) {
        return true;
      }
      return false;
    });
  }, [optimisticMoves, dataItem.driver?.id, index]);

  // Build set of ASSIGNED move IDs from this row's scenarios (for hiding optimistic blocks)
  // Only includes scenarios (assigned/accepted moves), NOT offeredScenarios (pending offers)
  // This ensures optimistic block stays visible while offer is pending
  const realMoveIds = useMemo(() => {
    const ids = new Set<number>();
    dataItem.scenarios.forEach((scenario) => {
      scenario.moves?.forEach((moveLink) => {
        if (moveLink.move?.id) {
          ids.add(moveLink.move.id);
        }
      });
    });
    // NOTE: Intentionally NOT including offeredScenarios here
    return ids;
  }, [dataItem.scenarios]);

  // Process driver schedule data
  const processedDriverSchedule = useMemo(() => {
    if (!driverId || !driverSchedule || driverSchedule.length === 0) {
      return undefined;
    }

    return processDriverSchedule(driverSchedule as any[], driverId);
  }, [driverSchedule, driverId]);

  // Calculate start of day for positioning
  const startOfDay = dayjs(date).add(12, 'hours').tz(timezoneOverride).startOf('day').format();

  return (
    <Box
      sx={{
        height: rowSize,
        position: 'relative',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Render driver schedule overlay for Lite tier - shows when driver is unavailable */}
      {isLiteTier(tier) && dataItem.driver?.id && (
        <DriverScheduleOverlay
          driverId={dataItem.driver.id.toString()}
          selectedDate={date}
          timezone={timezoneOverride}
          zoom={zoom}
        />
      )}

      {/* Render existing scenario blocks */}
      {dataItem.scenarios.map((scenario) => {
        const timeRange = getScenarioDisplayRange(scenario, date, timezoneOverride);
        if (!timeRange) return null;

        // Calculate position
        const leftPosition = getGapBetweenTimes(startOfDay, timeRange.startTime, zoom);

        return (
          <ScheduleScenarioBlock
            key={scenario.id}
            scenario={scenario}
            leftPosition={leftPosition}
            onClick={onScenarioClick}
            zoom={zoom}
          />
        );
      })}

      {/* Render offered scenarios */}
      {dataItem.offeredScenarios?.map((scenario) => {
        const timeRange = getScenarioDisplayRange(scenario, date, timezoneOverride);
        if (!timeRange) return null;

        // Calculate position
        const leftPosition = getGapBetweenTimes(startOfDay, timeRange.startTime, zoom);

        return (
          <ScheduleOfferedScenarioBlock
            key={`offered-${scenario.id}`}
            scenario={scenario}
            leftPosition={leftPosition}
            onClick={onScenarioClick}
            zoom={zoom}
          />
        );
      })}

      {/* Render optimistic (skeleton) moves for moves being created */}
      {driverOptimisticMoves.map((optimisticMove) => {
        // Calculate position based on start time
        const leftPosition = getGapBetweenTimes(
          startOfDay,
          optimisticMove.startTime.toISOString(),
          zoom
        );

        // Check if the real move has arrived in the data (use global set from timeline, not row-level)
        // This is important because the scenario may appear in a different row initially
        // Only check assigned moves (scenarios), not offered moves (offeredScenarios)
        const moveIdsToCheck = globalRealMoveIds || realMoveIds;
        const isRealMovePresentGlobally = optimisticMove.actualMoveId
          ? moveIdsToCheck.has(optimisticMove.actualMoveId)
          : false;

        const isRealMovePresentInRowOffered = optimisticMove.actualMoveId
          ? (dataItem.offeredScenarios || []).some((scenario) =>
              scenario.moves?.some((moveLink) => moveLink.move?.id === optimisticMove.actualMoveId)
            )
          : false;

        // Fade the optimistic block as soon as the real move is present anywhere on the
        // timeline (global assignment) or as an offered scenario in this row. This is NOT
        // scoped to this row's assigned scenarios: a created move may render on a different
        // driver row than the user selected, which previously left the tile stuck.
        const isRealMovePresent = isRealMovePresentInRowOffered || isRealMovePresentGlobally;

        return (
          <ScheduleOptimisticBlock
            key={`optimistic-${optimisticMove.id}`}
            optimisticMove={optimisticMove}
            leftPosition={leftPosition}
            zoom={zoom}
            isRealMovePresent={isRealMovePresent}
          />
        );
      })}

      {/* Render planning overlay when a planning address is selected */}
      {planningLane && onTimeSlotClick && (
        <SchedulePlanningOverlay
          lane={planningLane}
          selectedDate={date}
          zoom={zoom}
          timezoneOverride={timezoneOverride}
          onTimeSlotClick={(startTime) => onTimeSlotClick(startTime, dataItem.driver?.id)}
          assignedScenarios={dataItem.scenarios}
          offeredScenarios={dataItem.offeredScenarios || []}
          bufferMinutes={moveBufferMinutes}
          dayStartTime={dayStartTime}
          dayEndTime={dayEndTime}
          algorithm={algorithm}
          driverIndex={index}
          driverCount={driverCount}
          allDriverScenarios={allDriverScenarios}
          tier={tier}
          driverSchedule={processedDriverSchedule}
          offsetIncrementMinutes={offsetIncrementMinutes}
          offsetMaxMinutes={offsetMaxMinutes}
          scenarioKey={scenarioKey}
          driverId={dataItem.driver?.id}
        />
      )}
    </Box>
  );
};

export default ScheduleAreaRow;
